import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../App';
import { TrendingUp, Users, DollarSign, BarChart3, PieChart, Repeat } from 'lucide-react';

function SimpleBar({ data, maxVal, color = '#FF8C42' }) {
  if (!data || Object.keys(data).length === 0) return <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No data yet</p>;
  const max = maxVal || Math.max(...Object.values(data), 1);
  return (
    <div className="space-y-2">
      {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
        <div key={k} className="flex items-center gap-3">
          <span className="text-xs w-32 truncate text-right" style={{ color: 'var(--text-secondary)' }}>{k}</span>
          <div className="flex-1 h-6 rounded-lg overflow-hidden" style={{ background: 'var(--border-color)' }}>
            <div className="h-full rounded-lg transition-all duration-500" style={{ width: `${Math.max(4, (v / max) * 100)}%`, background: color }} />
          </div>
          <span className="text-xs font-semibold w-16" style={{ color: 'var(--text-primary)' }}>
            {typeof v === 'number' && v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : v}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function DenAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/admin/analytics').then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <div className="skeleton h-10 w-48 rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
      <div className="grid lg:grid-cols-2 gap-6">{[1,2,3,4].map(i => <div key={i} className="skeleton h-64 rounded-2xl" />)}</div>
    </div>
  );

  return (
    <div data-testid="den-analytics">
      <h1 className="font-outfit text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Analytics & Reports</h1>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Customers', value: data?.totalCustomers || 0, icon: Users, color: '#8B5CF6' },
          { label: 'Avg Order Value', value: `₹${data?.avgOrderValue || 0}`, icon: DollarSign, color: '#22C55E' },
          { label: 'Returning Customers', value: data?.returningCustomers || 0, icon: Repeat, color: '#FF8C42' },
          { label: 'Retention Rate', value: `${data?.retentionRate || 0}%`, icon: TrendingUp, color: '#20B2AA' },
        ].map((kpi, i) => (
          <div key={i} className="petfolk-card p-5" data-testid={`analytics-kpi-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <kpi.icon size={18} style={{ color: kpi.color }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{kpi.label}</span>
            </div>
            <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue by Store */}
        <div className="petfolk-card p-6" data-testid="chart-revenue-by-node">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-petfolk-orange" />
            <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>Revenue by Store</h3>
          </div>
          <SimpleBar data={data?.revenueByNode} color="#FF8C42" />
        </div>

        {/* Bookings by Store */}
        <div className="petfolk-card p-6" data-testid="chart-bookings-by-node">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-petfolk-purple" />
            <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>Bookings by Store</h3>
          </div>
          <SimpleBar data={data?.bookingsByNode} color="#8B5CF6" />
        </div>

        {/* Revenue by Service */}
        <div className="petfolk-card p-6" data-testid="chart-revenue-by-service">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={18} className="text-green-500" />
            <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>Revenue by Service</h3>
          </div>
          <SimpleBar data={data?.revenueByService} color="#22C55E" />
        </div>

        {/* Bookings by Source */}
        <div className="petfolk-card p-6" data-testid="chart-bookings-by-source">
          <div className="flex items-center gap-2 mb-4">
            <PieChart size={18} className="text-petfolk-teal" />
            <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>Bookings by Channel</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(data?.bookingsBySource || {}).map(([src, count]) => {
              const total = Object.values(data?.bookingsBySource || {}).reduce((a, b) => a + b, 1);
              const pct = Math.round((count / total) * 100);
              const colors = { WEB: '#FF8C42', WHATSAPP: '#25D366', CALL: '#8B5CF6', WALKIN: '#20B2AA' };
              return (
                <div key={src} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: colors[src] || '#6B7280' }} />
                  <span className="text-sm flex-1" style={{ color: 'var(--text-secondary)' }}>{src}</span>
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{count}</span>
                  <span className="text-xs w-10 text-right" style={{ color: 'var(--text-secondary)' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
          {/* Species Split */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
            <h4 className="font-outfit font-semibold text-sm mb-3" style={{ color: 'var(--text-primary)' }}>Species Split</h4>
            <div className="flex gap-4">
              {Object.entries(data?.bookingsBySpecies || {}).map(([sp, count]) => (
                <div key={sp} className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: sp === 'DOG' ? 'rgba(255,140,66,0.08)' : 'rgba(139,92,246,0.08)' }}>
                  <span className="text-lg">{sp === 'DOG' ? '🐕' : '🐈'}</span>
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
